import { Combobox } from "@headlessui/react";
import { forwardRef, SelectHTMLAttributes, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Controller } from "react-hook-form";
import { faCheck, faUpDown } from "@fortawesome/pro-solid-svg-icons";
import { Key } from "../../shared/models/Key";
import classNames from "classnames";

type InternalMultiSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "name"
> & {
  label: string;
  className?: string;
  data: Key<string | number>[];
  value?: (string | number | undefined)[];
  onChange: (values: (string | number)[]) => void;
  displayText: (
    data: Key<string | number>[],
    value?: (string | number | undefined)[]
  ) => string;
  onBlurSubmit?: () => void;
};

type MultiSelectProps = Omit<InternalMultiSelectProps, "value" | "onChange"> & {
  name: string;
};

// ref is required, but intentionally unused
// noinspection JSUnusedLocalSymbols
export const MultiSelect = forwardRef<HTMLSelectElement, MultiSelectProps>(
  (
    { name, ...rest }: MultiSelectProps, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ) => {
    return (
      <Controller
        name={name}
        render={({ field }) => <InternalMultiSelect {...rest} {...field} />}
      />
    );
  }
);

// ref is required, but intentionally unused
// noinspection JSUnusedLocalSymbols
export const InternalMultiSelect = forwardRef<
  HTMLSelectElement,
  InternalMultiSelectProps
>(
  (
    {
      label,
      className,
      data,
      onChange,
      disabled,
      value,
      displayText,
      onBlurSubmit,
    }: 
    InternalMultiSelectProps, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ) => {
    const [query, setQuery] = useState<string>("");
    const [showQuery, setShowQuery] = useState<boolean>(false);
    const [filtered, setFiltered] = useState<typeof data>(data);

    useEffect(() => {
      setQuery("");
    }, [data]);

    useEffect(() => {
      setFiltered(
        query === ""
          ? data
          : data?.filter((l) =>
            l.name
              ?.toLowerCase()
              .replace(/\s+/g, "")
              .includes(query.toLowerCase().replace(/\s+/g, ""))
          )
      );
    }, [data, query, value]);

    const handleChange = (event: never) => {
      onChange(event);
      setQuery("");
      setShowQuery(false);
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
      if (query) {
        return;
      }

      // If the user blurred onto a specific target inside the Combobox,
      // and want to keep the dropdown open for that interaction
      if (e.relatedTarget) {
        const targetElement = e.relatedTarget as HTMLElement;
        // This is super hacky but it works
        if (
          targetElement.classList.contains("pointer") &&
          targetElement.classList.contains("bg-gray-300")
        ) {
          // User still inside the Combobox or focusing another input => do nothing
          return;
        }
      }

      setShowQuery(false);

      // Call your blur-submission callback used in Brand multiselect in Wingman Admin
      onBlurSubmit?.();
    };

    return (
      <div className={className}>
        <div className="relative w-full select-none">
          <Combobox
            multiple
            onChange={handleChange}
            disabled={disabled}
            value={value ?? []}
          >
            <Combobox.Label className="text-sm">{label}</Combobox.Label>
            <div className={"border pl-2 rounded py-0.5 border-gray-400"}>
              <div className="flex">
                <Combobox.Input
                  onChange={(event) => setQuery(event.target.value)}
                  displayValue={() =>
                    showQuery ? query : displayText(data, value)
                  }
                  className="w-full"
                  onFocus={() => setShowQuery(true)}
                  onBlur={handleBlur}
                />
                <Combobox.Button>
                  <div className="pointer-events-none right-0 top-0 px-2">
                    <FontAwesomeIcon icon={faUpDown} />
                  </div>
                </Combobox.Button>
              </div>
              <Combobox.Options
                className="absolute z-40 max-h-96 w-full bg-white
              border border-gray-400 select-none overflow-y-auto left-0 shadow-lg"
              >
                {filtered.map((f, i) => (
                  <Combobox.Option
                    key={i}
                    value={f.id}
                    className={({ active }) =>
                      classNames(
                        " px-2 pointer",
                        active ? "bg-gray-300" : "bg-white"
                      )
                    }
                  >
                    {value?.some((s) => s === f.id) && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className={"text-unleashed-pink"}
                      />
                    )}
                    <span
                      className={
                        value?.some((s) => s === f.id) ? "ml-1" : "ml-5"
                      }
                    >
                      {f.name}
                    </span>
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </div>
          </Combobox>
        </div>
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
InternalMultiSelect.displayName = "InternalMultiSelect";


